import { useState, useEffect, useMemo } from "react";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import DimensionUnitsEnum from "@models/DimensionUnitsEnum";
import { convertDimension } from "@utils/DimensionConversion";
import "./DimensionWidget.scss";

type Props = {
    type: "depth" | "width" | "height";
    fn: (value: number) => void;
    value: number;
    units: "metric" | "imperial";
    compact?: boolean;
    catalogViewer?: boolean;
    buttonOnLeft?: boolean;
    targetUnitId?: DimensionUnitsEnum;
};

export const DimensionWidget = (props: Props) => {
    const { type, fn, value, units, compact = false, catalogViewer = false, buttonOnLeft = false, targetUnitId } = props;

    // Unified state for major and minor units
    const [majorUnit, setMajorUnit] = useState(0);
    const [minorUnit, setMinorUnit] = useState(0);

    // State for display mode (compound units like ft/in or single unit like inches)
    const [showCompoundUnits, setShowCompoundUnits] = useState(true);

    // Dynamic labels based on units
    const majorUnitLabel = units === "imperial" ? "ft" : "m";
    const minorUnitLabel = units === "imperial" ? "in" : "cm";
    const majorToMinorRatio = units === "imperial" ? 12 : 100;

    // Calculate total values
    const totalMinorUnits = majorUnit * majorToMinorRatio + minorUnit;

    // Determine the display unit based on the unit system
    const displayUnit = useMemo(() => {
        if (units === "imperial") {
            return DimensionUnitsEnum.INCHES;
        } else {
            return DimensionUnitsEnum.CENTIMETERS;
        }
    }, [units]);

    // Initialize values based on the incoming value and units
    useEffect(() => {
        // Convert incoming value from target unit to display unit
        const displayValue = targetUnitId ? convertDimension(value, targetUnitId, displayUnit) : value;

        if (units === "imperial") {
            // Display value is in inches
            const totalInches = displayValue;
            const ft = Math.floor(totalInches / 12);
            const inc = totalInches % 12;
            setMajorUnit(ft);
            setMinorUnit(inc);
        } else {
            // Display value is in centimeters
            const totalCm = displayValue;
            const m = Math.floor(totalCm / 100);
            const cm = totalCm % 100;
            setMajorUnit(m);
            setMinorUnit(cm);
        }
    }, [value, units, targetUnitId, displayUnit]);

    const handleUnitChange = (newMajor: number, newMinor: number) => {
        // Prevent negative values
        const validMajor = Math.max(0, newMajor);
        const validMinor = Math.max(0, newMinor);

        // If user enters more than the major-to-minor ratio, switch to single unit display
        if (validMinor > majorToMinorRatio) {
            setShowCompoundUnits(false);
            // Keep the values as entered for now
            setMajorUnit(validMajor);
            setMinorUnit(validMinor);
        } else {
            setMajorUnit(validMajor);
            setMinorUnit(validMinor);
        }

        // Calculate total in minor units (display units)
        const total = validMajor * majorToMinorRatio + validMinor;

        // Convert from display unit to target unit if specified
        if (targetUnitId) {
            const targetValue = convertDimension(total, displayUnit, targetUnitId);
            fn(targetValue);
        } else {
            // Legacy behavior - imperial returns inches, metric returns cm
            if (units === "imperial") {
                fn(total);
            } else {
                fn(total);
            }
        }
    };

    const handleSingleUnitChange = (newValue: number) => {
        // Prevent negative values
        const validValue = Math.max(0, newValue);

        // Update major and minor units from total
        const major = Math.floor(validValue / majorToMinorRatio);
        const minor = validValue % majorToMinorRatio;
        setMajorUnit(major);
        setMinorUnit(minor);

        // Convert from display unit to target unit if specified
        if (targetUnitId) {
            const targetValue = convertDimension(validValue, displayUnit, targetUnitId);
            fn(targetValue);
        } else {
            // Legacy behavior - imperial returns inches, metric returns cm
            fn(validValue);
        }
    };

    const toggleDisplayMode = () => {
        setShowCompoundUnits(!showCompoundUnits);
    };

    const conversionButton = (
        <div className={compact ? `${catalogViewer ? "" : "me-1"}` : "me-2"}>
            <button
                type="button"
                className={`btn btn-outline-secondary ${compact ? "btn-sm" : ""}`}
                onClick={toggleDisplayMode}
                title={showCompoundUnits ? `Convert to ${minorUnitLabel}` : `Convert to ${majorUnitLabel}/${minorUnitLabel}`}>
                <i className="fa fa-exchange" aria-hidden="true"></i>
                {!compact && (
                    <span className="ms-1">{showCompoundUnits ? `→ ${minorUnitLabel}` : `→ ${majorUnitLabel}/${minorUnitLabel}`}</span>
                )}
            </button>
        </div>
    );

    return (
        <div className={`DimensionWidget ${compact ? "compact" : ""}`}>
            <div className={compact ? "" : "form-group"}>
                {!compact && <label className="control-label">{type.charAt(0).toUpperCase() + type.slice(1)}</label>}

                <div className="d-flex align-items-start">
                    {buttonOnLeft && conversionButton}
                    <div className="flex-grow-1">
                        <div className={compact ? `d-flex ${catalogViewer ? "flex-column" : "flex-row min-w-15r"}` : "row"}>
                            {compact && units === "metric" && (
                                <div className="me-2">
                                    <small className="text-muted">{type}:</small>
                                </div>
                            )}
                            <div className={compact ? "" : "col-auto"}>
                                <div className={`input-group ${compact ? "input-group-sm" : ""}`}>
                                    <OwFnInput
                                        name={`${type}-major`}
                                        type="number"
                                        value={showCompoundUnits ? majorUnit : 0}
                                        fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const newMajor = Number(e.target.value);
                                            if (!showCompoundUnits && newMajor > 0) {
                                                setShowCompoundUnits(true);
                                                handleUnitChange(newMajor, totalMinorUnits % majorToMinorRatio);
                                            } else {
                                                handleUnitChange(newMajor, minorUnit);
                                            }
                                        }}
                                        min={0}
                                        step={1}
                                        enableDebounce={true}
                                        debounceMs={300}
                                        noOuterDiv={true}
                                        className={compact ? "form-control-sm" : ""}
                                    />
                                    <span className="input-group-text">{majorUnitLabel}</span>
                                </div>
                            </div>
                            <div className={compact ? `${catalogViewer ? "" : "ms-1"}` : "col-auto"}>
                                <div className={`input-group ${compact ? "input-group-sm" : ""}`}>
                                    <OwFnInput
                                        name={`${type}-minor`}
                                        type="number"
                                        value={
                                            showCompoundUnits ? parseFloat(minorUnit.toFixed(2)) : parseFloat(totalMinorUnits.toFixed(2))
                                        }
                                        fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const newMinor = Number(e.target.value);
                                            if (showCompoundUnits) {
                                                handleUnitChange(majorUnit, newMinor);
                                            } else {
                                                handleSingleUnitChange(newMinor);
                                            }
                                        }}
                                        min={0}
                                        step="any"
                                        enableDebounce={true}
                                        debounceMs={300}
                                        noOuterDiv={true}
                                        className={compact ? "form-control-sm" : ""}
                                    />
                                    <span className="input-group-text">{minorUnitLabel}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    {!buttonOnLeft && conversionButton}
                </div>
            </div>
        </div>
    );
};
